import GoogleMapReact from "google-map-react";
import { useCallback, useEffect, useState } from "react";
import {
  COLOR_GRAY,
  COLOR_GREEN,
  COLOR_RED,
  CONSTANTS,
} from "src/constants/constants";
import { Location, LatLng, Order } from "src/utils/interfaces/interfaces";
import { EventMarker } from "../UI/Markers/EventMarker";
import TrackerMarker from "../UI/Markers/Tracker/TrackerMarker";
import { CurrentLocationMarker, LiveMarker, StartMarker, EndMarker } from "../UI/Markers/OrderMarkers";
import { Button } from "@heroui/react";
import { FaEllipsisVertical } from 'src/icons';

export interface IDashboardMapProps {
  defaultCenter?: LatLng
  defaultZoom?: number;
  zoom?: number;
  routeOrigin?: Location;
  routeDestination?: Location;
  currentLocation?: LatLng;
  triggerDeviation?: Function;
  triggerCorrectPath?: Function;
  trackerPath?: Array<LatLng>;
  hideCurrentLocation?: Boolean;
  isCompleted?: Boolean;
  events?: Array<any>;
  route?: Array<LatLng> | boolean;
  historyRoute?: any;
  trackerData?: any;
  onComplete?: () => void;
  mapTheme?: any;
  order?: Order;
}

export function DashboardMap({
  defaultCenter = {
    lat: 25.2048,
    lng: 55.2708,
  },
  defaultZoom = 12,
  zoom = 12,
  routeOrigin = {
    latitude: 25.188163682053897,
    longitude: 55.2989263100694,
  },
  routeDestination = {
    latitude: 25.187040537554285,
    longitude: 55.29678837311032,
  },
  currentLocation,
  triggerDeviation = () => {},
  triggerCorrectPath = () => {},
  trackerPath,
  events,
  hideCurrentLocation = false,
  isCompleted = false,
  route = false,
  historyRoute,
  onComplete = () => undefined,
  mapTheme,
  trackerData,
  order,
}: IDashboardMapProps) {
  const [googleApiObj, setIsGoogleApiLoadedObj] = useState<any>(null);
  const [designatedRoute, setDesignatedRoute] = useState<any>(null);
  const [currentMapTypeId, setCurrentMapTypeId] = useState<string>("roadmap");
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const tolerence = 0.0005;
  // const [lastLocationOnEdge, setLastLocationOnEdge] = useState<Cordinates>({
  //   lat: routeOrigin.latitude,
  //   lng: routeOrigin.longitude,
  // });

  const [travelledPath, setTravelledPath] = useState<
    Array<LatLng> | undefined
  >(trackerPath);

  const toggleDropdown = () => setDropdownOpen(!isDropdownOpen);



  const mapCenter =
    currentLocation && !(currentLocation.lat === 0 && currentLocation.lng === 0)
      ? currentLocation
      : defaultCenter;

  const reCenter = () => {
    if (googleApiObj) {
      const { map } = googleApiObj;
      if (!hideCurrentLocation) {
        if (
          currentLocation &&
          !(currentLocation.lat === 0 && currentLocation.lng === 0)
        ) {
          map.setCenter(currentLocation);
        } else {
          map.setCenter(defaultCenter);
        }
      }
    }
  };

  const _onTrackerUpdate = useCallback(
    (map: any, maps: any) => {
      // let _travelledpath = travelledPath;
      // if (
      //   getDistance(maps, currentLocation, {
      //     lat: routeDestination.latitude,
      //     lng: routeDestination.longitude,
      //   }) < 0.0005
      // )
      // currentLocation && _travelledpath?.push(currentLocation);
      // setTravelledPath(_travelledpath);
      plotTravelledPath();
      plotHistoryPath();
      // completedPath.bringToFront();
      // fitBounds(map, maps, _travelledpath);
      //
      // Check if current location is on the route path
      //
      // Only check for deviation if designatedRoute and currentLocation are available
      if (designatedRoute && currentLocation) {
        if (
          !maps.geometry.poly.isLocationOnEdge(
            currentLocation,
            designatedRoute,
            tolerence
          )
        ) {
          triggerDeviation();
        } else {
          triggerCorrectPath();
        }
      } else {
        // Handle cases where designatedRoute or currentLocation is not available
        console.warn("designatedRoute or currentLocation is not defined.");
      }
      checkForOrderCompletion(maps);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      designatedRoute,
      currentLocation,
      triggerDeviation,
      triggerCorrectPath,
      travelledPath,
      setTravelledPath,
    ]
  );
  const getDistance = (maps: any, source: any, destination: any) => {
    // Check if source and destination objects have lat and lng properties
    if (!source || !source.lat || !source.lng) {
      console.error("Source is missing lat or lng properties:", source);
      return 0; // Return 0 or handle it as per your requirement
    }
  
    if (!destination || !destination.lat || !destination.lng) {
      console.error("Destination is missing lat or lng properties:", destination);
      return 0; // Return 0 or handle it as per your requirement
    }
  
    return maps.geometry?.spherical.computeDistanceBetween(
      new maps.LatLng(source.lat, source.lng),
      new maps.LatLng(destination.lat, destination.lng),
      10
    );
  };

  const _checkForDevaition = (maps: any, polyline: any) => {
    if (designatedRoute && currentLocation) {
      if (
        !maps.geometry.poly.isLocationOnEdge(
          currentLocation,
          designatedRoute,
          tolerence
        )
      ) {
        triggerDeviation();
      } else {
        triggerCorrectPath();
      }
    } else {
      console.warn(
        "Cannot check for deviation: designatedRoute or currentLocation is not defined."
      );
    }
  };

  const checkForOrderCompletion = (maps: any) => {
    const tolerence = 0.0008;
    const distance = getDistance(maps, currentLocation, {
      lat: routeDestination.latitude,
      lng: routeDestination.longitude,
    });
    if (distance < tolerence) {
      onComplete();
    }
  };

  const _renderRoute = (map: any, maps: any) => {
    setIsGoogleApiLoadedObj({
      map,
      maps,
    });
    if (route) {
      var polyline = new maps.Polyline({
        path: route,
        map: map,
      });
      setDesignatedRoute(polyline);
      const plottedRoute = new maps.Polyline({
        path: route,
        geodesic: true,
        strokeColor: isCompleted ? COLOR_RED : COLOR_GRAY,
        strokeOpacity: 1,
        strokeWeight: 7,
      });
      plottedRoute.setMap(map);
      // fitBounds(map, maps, route);
      // plotTravelledPath()
      // plotHistoryPath()
      // fitBounds(map, maps, _travelledpath);
      _checkForDevaition(maps, polyline);
    } else {
      setDesignatedRoute(null);
    }
  };

  const plotTravelledPath = () => {
    if (googleApiObj && travelledPath) {
      const { map, maps } = googleApiObj;
      let _travelledpath = travelledPath;

      if (
        currentLocation &&
        !(currentLocation.lat === 0 && currentLocation.lng === 0)
      ) {
        const lastPoint = _travelledpath[_travelledpath.length - 1];

        // Add this check to prevent pushing the same location
        if (
          !lastPoint ||
          lastPoint.lat !== currentLocation.lat ||
          lastPoint.lng !== currentLocation.lng
        ) {
          _travelledpath.push(currentLocation);
        }
      }

      setTravelledPath(_travelledpath);

      let completedPath = new maps.Polyline({
        path: _travelledpath,
        geodesic: true,
        strokeColor: isCompleted ? COLOR_GRAY : COLOR_GREEN,
        strokeOpacity: 1,
        strokeWeight: 7,
        zIndex: 10,
      });
      completedPath.setMap(map);
    }
  };

  const plotHistoryPath = () => {
    if (googleApiObj) {
      const { map, maps } = googleApiObj;

      const mainColor = "#3772FF";
      const borderColor = "#1F4AB4";

      let borderPath = new maps.Polyline({
        path: historyRoute,
        geodesic: true,
        strokeColor: borderColor,
        strokeOpacity: 1,
        strokeWeight: 6,
        zIndex: 19,
      });
      borderPath.setMap(map);

      let tripPath = new maps.Polyline({
        path: historyRoute,
        geodesic: true,
        strokeColor: mainColor,
        strokeOpacity: 1,
        strokeWeight: 4,
        zIndex: 20,
      });
      tripPath.setMap(map);
    }
  };

  useEffect(() => {
    if (googleApiObj && travelledPath) {
      const { map, maps } = googleApiObj;
      _renderRoute(map, maps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  useEffect(() => {
    plotTravelledPath();
    plotHistoryPath();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelledPath, historyRoute]);

  const _init = (map: any, maps: any) => {
    _renderRoute(map, maps);

    // Listen for changes in map type (roadmap or satellite)
    maps.event.addListener(map, "maptypeid_changed", () => {
      setCurrentMapTypeId(map.getMapTypeId()); // Update the current map type
    });
  };

  useEffect(() => {
    if (googleApiObj) {
      const { map, maps } = googleApiObj;
      _onTrackerUpdate(map, maps);
    }
  }, [currentLocation, _onTrackerUpdate, googleApiObj]);

  useEffect(() => {
    if (trackerPath) {
      var travelledPath: LatLng[] = [];
      trackerPath.forEach((history: any) => {
        if (history.latitude !== 0 && history.longitude !== 0) {
          travelledPath.push({ lat: history.latitude, lng: history.longitude });
        }
      });

      setTravelledPath(travelledPath);
    }
  }, [trackerPath]);

  const changeMapType = (type: string) => {
    if (googleApiObj) {
      const { map } = googleApiObj;
      map.setMapTypeId(type);
      setCurrentMapTypeId(type);
      setDropdownOpen(false);
    }
  };

  return (
    <div style={{ height: "100%", width: "100%" }}>
      {/* @ts-ignore */}
      <GoogleMapReact
        bootstrapURLKeys={{
          key: CONSTANTS.GOOGLE_JS_MAPS_KEY,
          libraries: ["geometry", "places"],
        }}
        // defaultCenter={currentLocation}
        center={mapCenter}
        defaultZoom={defaultZoom}
        zoom={zoom}
        options={(maps: any) => ({
          fullscreenControl: false,
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          clickableIcons: false,
          // mapTypeControlOptions: {
          //   style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
          //   position: maps.ControlPosition.TOP_RIGHT,
          //   mapTypeIds: ["roadmap", "satellite", "hybrid"],
          // },
          styles:
            currentMapTypeId === "roadmap"
              ? mapTheme || CONSTANTS.GOOGLE_MAP_THEME_STYLES_ALL
              : null,
        })}
        onGoogleApiLoaded={({ map, maps }) => {
          _init(map, maps);
        }}
        yesIWantToUseGoogleMapApiInternals
      >
        {routeDestination && (
          <EndMarker
            lat={routeDestination.latitude}
            lng={routeDestination.longitude}
          />
        )}
        {historyRoute && historyRoute.length > 0 && (
          <TrackerMarker
            lat={historyRoute[historyRoute?.length - 1]?.lat}
            lng={historyRoute[historyRoute?.length - 1]?.lng}
            plateNumber={
              order?.vehicle?.plateNumber?.replace("private-", "") || "N/A"
            }
            status={trackerData[trackerData?.length - 1]?.status}
            data={trackerData}
          />
        )}

        <StartMarker lat={routeOrigin.latitude} lng={routeOrigin.longitude} />
        {order?.status === "in_transit" && currentLocation &&
          !hideCurrentLocation &&
          !(currentLocation.lat === 0 && currentLocation.lng === 0) && (
            <LiveMarker lat={currentLocation.lat} lng={currentLocation.lng} />
          )}

         {order?.status !== "in_transit" && currentLocation &&
          !hideCurrentLocation &&
          !(currentLocation.lat === 0 && currentLocation.lng === 0) && (
            <CurrentLocationMarker lat={currentLocation.lat} lng={currentLocation.lng} />
          )}
        {events &&
          events.map((event, index) => {
            if (!event || !event.latitude || !event.longitude) {
              return null;
            }
            return (
              <EventMarker
                event={event}
                key={index}
                lat={event.latitude}
                lng={event.longitude}
              />
            );
          })}
      </GoogleMapReact>

      <div className="absolute bottom-24 right-4 z-10">
        <div className="relative">
          {/* Three-dot icon to toggle the dropdown */}
          <Button
          variant="shadow"
            className="rounded-full p-2 transition shadow-sm font-medium" style={{ backgroundColor: "var(--color-primary)", color: "var(--text-invert)" }}
            onClick={toggleDropdown}
            isIconOnly
          >
            <FaEllipsisVertical className="text-white" />
          </Button>

          {/* Dropdown menu with MUI radio buttons */}
          {isDropdownOpen && (
            <div className="absolute bottom-11 right-6 mt-2 w-36 bg-primary shadow-lg rounded-lg py-2">
              <p className="pl-4 pt-1 mb-1 text-sm text-white text-opacity-60">
                Map Type
              </p>
              <label className="flex items-center gap-2 px-4 py-1 text-white cursor-pointer">
                <input
                  type="radio"
                  name="dashboard-map-type"
                  checked={currentMapTypeId === "roadmap"}
                  onChange={() => changeMapType("roadmap")}
                />
                <span>Default</span>
              </label>
              <label className="flex items-center gap-2 px-4 py-1 text-white cursor-pointer">
                <input
                  type="radio"
                  name="dashboard-map-type"
                  checked={currentMapTypeId === "hybrid"}
                  onChange={() => changeMapType("hybrid")}
                />
                <span>Satellite</span>
              </label>
            </div>
          )}
        </div>
      </div>

      <img
        src="/assets/icons/re_center.svg"
        alt="Re center"
        className="absolute bottom-10 right-4 h-10 w-10 cursor-pointer"
        onClick={() => reCenter()}
      />
    </div>
  );
}
